import Customer from "../../customer/interfaces/Customer";
import Beneficiary from "../../customer/interfaces/Beneficiary";
import Prescriptor from "../../prescriptor/interfaces/Prescriptor";
import PrescriptionRenewal from "../../prescription/interfaces/prescription-renewal/PrescriptionRenewal";
import Cart from "./Cart";
import Product from "../../product/interfaces/Product";
import CustomerInformation from "@/core/interfaces/CustomerInformation";
import FileUrl from "@/core/interfaces/FileUrl";

/**
 * Enum for order content type.
 *
 * @enum OrderContentType
 */
export enum OrderContentType {
    RENEWAL = "renewal",
    CART = "cart",
    NEW_PRESCRIPTION = "new_prescription"
}

/**
 * Enum for order payment method.
 */
export enum OrderPaymentMethod {
    COUNTER = "counter",
    ONLINE = "online"
}

/**
 * Online payment attached to an order (Stripe Connect).
 *
 * @property {number} amount_total - Paid amount, in cents.
 */
export interface OnlinePayment {
    id: number;
    order_id: number;
    amount_total: number;
    currency: string;
    status: string;
    created_at: string;
}

/**
 * Enum for order status.
 */
export enum OrderStatusType {
    DELIVERED = "delivered",
    AVAILABLE = "available",
    ACCEPTED = "accepted",
    REJECTED = "rejected",
    PENDING = "pending"
}

export interface OrderIndex {
    id: number;
    status: OrderStatusType;
    content_type: OrderContentType;
    user_id?: number;
    customer: {
        id: number;
        information: CustomerInformation;
    },
    prescriptor?: Prescriptor;
    orderable: OrderableIndex;
    created_at: string;
    accepted_at?: string;
    rejected_at?: string;
    available_at?: string;
    delivered_at?: string;
    paid_at?: string | null;
    payment_method?: OrderPaymentMethod;
    online_payment?: OnlinePayment | null;
}

export interface OrderableIndex {
    id: number;
    type: string;
    prescription?: {
        id: number;
        key?: string;
        files?: FileUrl[];
    },
    products_count?: number;
    items_count?: number;
}

/**
 * Interface for orders
 *
 */
interface Order {
    /** The order status. **/
    status: OrderStatusType;
    /** The date it was accepted. **/
    accepted_at: string;
    /** The date it available. **/
    available_at: string;
    /** The content type. **/
    content_type: OrderContentType;
    /** The date it was created. **/
    created_at: string;
    /** The customer who made the order. **/
    customer: Customer,
    /** The beneficiary object if any. **/
    beneficiary?: Beneficiary;
    /** The date it delivered. **/
    delivered_at: string;
    /** The date it was rejected. **/
    rejected_at: string;
    /** The date it was paid online, if any. **/
    paid_at?: string | null;
    /** How the patient pays the order (counter or online). **/
    payment_method?: OrderPaymentMethod;
    /** The succeeded online payment, if any. **/
    online_payment?: OnlinePayment | null;
    /** The id. **/
    id: number;
    /** The prescriptor. **/
    prescriptor?: Prescriptor;
    /** The order polymorphic content. **/
    content: PrescriptionRenewal | Cart;
    /** The optional user comment. **/
    user_comment?: string;
    /** The optional operator comment. **/
    operator_comment?: string;
    /** The optional operator code. **/
    operator_code?: string;
}

/**
 * Order item interface.
 *
 * @property {Product} product - Ordered product.
 * @property {number} quantity - Ordered quantity.
 * @property {number} total_price - Total price.
 * @property {boolean} [in_stock] - If product is in stock.
 */
export interface OrderItem {
    product: Product;
    quantity: number;
    total_price?: number;
    in_stock?: boolean;
    offer?: OrderItemOffer;
}

export interface OrderItemOffer {
    label: string;
    name: string;
    start_at: string;
    end_at: string;
    offer_id: number;
    unit_price?: number;
    total_price?: number;
}

export default Order;
